import { DataAccess, ResponseMessage } from "../dataaccess/data.access";
import { EventService } from "./event.service";
import { S25Const } from "../util/s25-const";
import { S25Util } from "../util/s25-util";
import { BehaviorSubject } from "rxjs";

export class StandoutClassroomService {
    private static isRecurringCheckRunning = false;
    private static unreadChatsSubject = new BehaviorSubject<UnreadChat[]>([]);

    public static getChatLink() {
        return StandoutClassroomService.getRedirectUrl(`/init/chat/app`);
    }

    public static async getEventChatLink(eventId: number) {
        let eventLocator = await EventService.getEventLocator(eventId);
        eventLocator = eventLocator ? ` (${eventLocator})` : "";
        let channelName = (await EventService.getEventNameOrTitle(eventId)) + eventLocator;
        return StandoutClassroomService.getRedirectUrl(
            `/init/chat/${eventId}/app?title=${encodeURIComponent(channelName)}`,
        );
    }

    public static async getUnreadChatInfo() {
        let jwtStr = await StandoutClassroomService.getAuthToken();
        if (!jwtStr) {
            return [];
        }
        let [unread, _error] = await S25Util.Maybe(
            DataAccess.get<UnreadChats>(StandoutClassroomService.getWsUrl("/data/qk/user/channels/unread.json"), null, {
                Authorization: "Bearer " + jwtStr,
            }),
        );
        unread ??= {};
        unread.qkChannelTO ??= [];
        return unread.qkChannelTO;
    }

    /**
     * Returns a promise that either kicks off the first request + recursion
     * OR sees that it's already going and returns the cached response
     */
    public static async getUnreadChatsWithRecurringCheck(): Promise<UnreadChat[]> {
        if (!StandoutClassroomService.isRecurringCheckRunning) {
            // Start the first request and set up recursion
            StandoutClassroomService.isRecurringCheckRunning = true;
            await StandoutClassroomService.startUnreadChatsCheck();
        }
        return StandoutClassroomService.unreadChatsSubject.value;
    }

    /**
     * Returns an observable that emits whenever unread chats are updated
     */
    public static getUnreadChatsObservable() {
        return StandoutClassroomService.unreadChatsSubject.asObservable();
    }

    private static async startUnreadChatsCheck(): Promise<void> {
        try {
            const unreadChats = await StandoutClassroomService.getUnreadChatInfo();

            // Emit the updated unread chats to all subscribers
            StandoutClassroomService.unreadChatsSubject.next(unreadChats);

            // Set up the next check in 5 minutes
            setTimeout(
                () => {
                    StandoutClassroomService.startUnreadChatsCheck();
                },
                5 * 60 * 1000,
            );
        } catch (error) {
            console.error("Error fetching unread chats:", error);
            StandoutClassroomService.isRecurringCheckRunning = false; // Reset on error
        }
    }

    private static getRedirectUrl(subPath: string) {
        let url = DataAccess.getUrl(`/redirect/app?app=soc&redirect=${encodeURIComponent(subPath)}`);
        if (window.location.hostname === "localhost") {
            url += "&fullToken=true";
        }
        return url;
    }

    private static getBaseWsUrl() {
        if (S25Const.isWebDevInstance) {
            return `http://25live-web-dev.unival.com/${S25Const.instanceId}/standout-classroom`;
        }
        return `https://series25.com/${S25Const.instanceId}/standout-classroom`;
    }

    private static getWsUrl(path: string = ""): string {
        if (!path.startsWith("/")) {
            path = "/" + path;
        }
        return this.getBaseWsUrl() + path;
    }

    private static async getAuthToken(): Promise<string> {
        let offsetSec = 60; // 1 minute offset to ensure the token stays valid during the request
        let jwtStr = localStorage.getItem("socJwt") || "";
        let jwtParsed = S25Util.parseJwt(jwtStr);
        if (jwtParsed?.exp && jwtParsed.exp > Date.now() / 1000 + offsetSec) {
            return jwtStr;
        }
        // if the token is not valid or expired (or too close to expired for comfort), we need to get a new one
        return await StandoutClassroomService.getFreshSocJwt();
    }

    private static async getFreshSocJwt() {
        let fullToken = "";
        if (window.location.hostname === "localhost") {
            fullToken += "?fullToken=true";
        }
        let resp = await DataAccess.get<ResponseMessage>(`/soc/jwt.json${fullToken}`);
        if (resp?.message) {
            localStorage.setItem("socJwt", resp.message);
            return resp.message;
        }
        return "";
    }
}

// data shape pulled from: https://collegenet.atlassian.net/browse/SOC-612
export interface UnreadChats {
    qkChannelTO?: UnreadChat[];
}

export interface UnreadChat {
    channelId?: number;
    unreadCount?: number;
    creatorId?: number;
    title?: string;
    type?: "public" | "private";
    uuid?: string;
    externalId?: string;
    createDt?: string;
    lastModDt?: string;
}
